import { Badge } from "@/components/ui/badge";
import { useAdminTenants } from "@/features/admin";
import { useBackOfficeAccess } from "@/features/auth/back-office-access";
import { ClientScopeProvider } from "@/features/utils/api-scope";
import { cn } from "@/lib/utils";
import { CaretLeft } from "@phosphor-icons/react";
import { Link, NavLink, Outlet, useParams } from "react-router-dom";
import { AdminPageLayout } from "../layout";
import { DeviceBaseDialogHost } from "../../backoffice/devices/components/base-dialog";

/**
 * The tabs of a client's file. Ordered by how often a question lands on them.
 *
 * `read`: also shown to the read-only account (sales) — what the client sees of
 * its own activity, nothing of its installation (`AdminOnly` in the router).
 */
const TABS: { to: string; label: string; read?: boolean }[] = [
  { to: "overview", label: "Vue d'ensemble", read: true },
  { to: "dashboard", label: "Dashboard", read: true },
  { to: "calls", label: "Appels", read: true },
  { to: "voip", label: "Comptes VoIP" },
  { to: "bases", label: "Bases" },
  { to: "users", label: "Utilisateurs & accès" },
  { to: "channels", label: "Canaux & numéros" },
  { to: "data", label: "Données & historique" },
];

/**
 * One client, everything about them, in the screens they use themselves.
 *
 * This is the piece the platform back-office was missing: eleven cross-tenant
 * tables and no way to look at *a client*. Answering "what is going on with
 * this one" meant opening three of those tables and filtering each by name.
 *
 * The tabs below mount the very components the client's own back-office mounts
 * — same tables, same dialogs, same validation — pointed at this client through
 * {@link ClientScopeProvider} and at the staff mount of the API. That is what
 * makes the two screens agree by construction: when a client says "the field is
 * greyed out", staff are looking at the same field. The only deliberate
 * difference is that some columns carry more, because the person here is
 * answering a question rather than managing their own phones.
 */
export function AdminClientPage() {
  const { clientId = "" } = useParams<{ clientId: string }>();
  const { data: tenants = [] } = useAdminTenants(30, clientId);
  const access = useBackOfficeAccess();

  const tenant = tenants.find((item) => item.id === clientId);
  const tabs = TABS.filter((tab) => access === "admin" || tab.read);

  return (
    <ClientScopeProvider clientId={clientId} scope="staff">
      <AdminPageLayout
        title={tenant?.name || "Client"}
        description={
          access === "admin"
            ? "La fiche du client : son activité, ses postes, ses bases, ses accès et ses canaux."
            : "La fiche du client : son activité, son dashboard et ses appels, tels qu'il les voit."
        }
        actions={
          <Link
            to="/admin/clients"
            className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm"
          >
            <CaretLeft className="h-4 w-4" />
            Tous les clients
          </Link>
        }
      >
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="font-mono text-xs">
            {clientId}
          </Badge>
          {tenant && (
            <span className="text-muted-foreground text-sm">
              {tenant.users} utilisateur{tenant.users > 1 ? "s" : ""}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1 border-b">
          {tabs.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              className={({ isActive }) =>
                cn(
                  "-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                )
              }
            >
              {tab.label}
            </NavLink>
          ))}
        </div>

        <Outlet />

        {/*
          Once for the file rather than per tab: the handsets tab opens a base
          from its action menu and the bases tab opens one from its rows, and
          two mounts on one prompt would render the dialog twice.
        */}
        {access === "admin" && <DeviceBaseDialogHost />}
      </AdminPageLayout>
    </ClientScopeProvider>
  );
}
